<script setup>
import {computed, onMounted, ref, watch} from 'vue'
import {useRouter} from 'vue-router'
import {codePreview, formatDate, showSuccessMessage, translateRole, validatePhoneNumber} from '@/helper';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import Textarea from '@/components/forms/Textarea.vue';
import Select from '@/components/forms/Select.vue';
import {authData} from '@/services/AuthService';
import Input from "@/components/forms/Input.vue";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import IntermediaryService from "@/services/IntermediaryService";
import ModalDialog from "@/components/modals/ModalDialog.vue";
import IntermediaryPersonsService from "@/services/IntermediaryPersonsService";
import {useI18n} from 'vue-i18n';

const {t} = useI18n();
const router = useRouter();

const props = defineProps({
  intermediaryPerson: {type: Object, required: false},
  intermediary: {type: Object, required: true},
});

const availableIntermediaryPersons = ref(null);
const visibleFields = ref([]);
const intermediaryPerson = ref({surname: '', firstname: '', email: '', phone: '', password: '', password_conf: '', role: 'INTERMEDIARY', parent_id: null, mentor_firstname: '', mentor_surname: '', registration_number: ''});
const intermediary = ref({});
const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);
const availableRoles = computed(() => [
  {'value': 'INTERMEDIARY', 'label': translateRole('INTERMEDIARY')},
  {'value': 'INTERMEDIARY_ADMIN', 'label': translateRole('INTERMEDIARY_ADMIN')},
  {'value': 'INTERMEDIARY_MANAGER', 'label': translateRole('INTERMEDIARY_MANAGER')},
  {'value': 'INTERMEDIARY_LEADER', 'label': translateRole('INTERMEDIARY_LEADER')},
]);
// IMA-510: pole "role" smí měnit jen ADMIN/SUPERADMIN/INTERMEDIARY_ADMIN (libovolná role) nebo INTERMEDIARY_MANAGER
// (jen INTERMEDIARY/INTERMEDIARY_LEADER, viz IntermediaryPersonService::save() na BE) - select proto zamykáme resp.
// zakazujeme jednotlivé <option>, aby uživatel bez oprávnění nedostal 403 až po odeslání formuláře.
const FULL_ROLE_ASSIGNERS = ['ADMIN', 'SUPERADMIN', 'INTERMEDIARY_ADMIN'];
const LIMITED_ROLE_ASSIGNERS = ['INTERMEDIARY_MANAGER'];
const LIMITED_ASSIGNABLE_ROLES = ['INTERMEDIARY', 'INTERMEDIARY_LEADER'];
const canAssignRole = computed(() => FULL_ROLE_ASSIGNERS.includes(authData?.loggedUser?.role) || LIMITED_ROLE_ASSIGNERS.includes(authData?.loggedUser?.role));
const isRoleOptionDisabled = (roleValue) => {
  if (FULL_ROLE_ASSIGNERS.includes(authData?.loggedUser?.role)) {
    return false;
  }
  if (LIMITED_ROLE_ASSIGNERS.includes(authData?.loggedUser?.role)) {
    return !LIMITED_ASSIGNABLE_ROLES.includes(roleValue);
  }
  return true;
};

const passwordMismatch = ref(false);

watch([() => intermediaryPerson.value.password, () => intermediaryPerson.value.password_conf], () => {
  passwordMismatch.value = intermediaryPerson.value.password && intermediaryPerson.value.password_conf && intermediaryPerson.value.password !== intermediaryPerson.value.password_conf;
});

onMounted(() => {
  if (props.intermediaryPerson) {
    intermediaryPerson.value = {...props.intermediaryPerson};
  }

  if (props.intermediary) {
    intermediary.value = {...props.intermediary};
  }

  IntermediaryPersonsService.getOfIntermediary(props.intermediary.id).then((response) => {
    visibleFields.value = response.visible_fields ?? [];

    let list = [{id: null, label: t('createUpdateIntermediaryPersonModal.without_manager')}];
    response.persons.forEach((item) => {
      if (!intermediaryPerson.value?.id || intermediaryPerson.value.id !== item.id) {
        list.push(item);
      }
    });

    availableIntermediaryPersons.value = list;
  });
});

const closeModalWith = (value) => {
  setTimeout(() => {
    if (value === 'confirm') {
      try {
        IntermediaryPersonsService.saveIntermediaryPerson(intermediaryPerson.value, intermediary?.value.id).then(response => {
          if (response.errors) {
            return;
          }
          emit('confirm', intermediaryPerson.value);
          showSuccessMessage(response.message);
          //fetchClients();
          intermediaryPerson.value = response.intermediary_person;
          //fetchDocumentTemplates(datatable.body);
        });
        //isOpen.value = false;
      } catch (e) {
      }
    } else {
      emit('cancel');
      isOpen.value = false;
    }
  }, 300);
}

function deleteIntermediaryPerson() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('createUpdateIntermediaryPersonModal.delete_intermediary_person'),
    message: t('createUpdateIntermediaryPersonModal.delete_intermediary_person_confirm'),
  });
  onConfirm(() => {
    IntermediaryPersonsService.deleteIntermediaryPerson(intermediaryPerson.value.id).then(() => {
      isOpen.value = false;
      showSuccessMessage(t('createUpdateIntermediaryPersonModal.intermediary_person_deleted'));
      router.push({name: 'IntermediaryPeople'});
    });
  });
  reveal();
}

function openModal() {
  isOpen.value = true
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle v-if="intermediaryPerson.id" as="h3" class="modal-title">
                {{ t('createUpdateIntermediaryPersonModal.intermediary_person_edit') }}
              </DialogTitle>
              <DialogTitle v-else as="h3" class="modal-title">
                {{ t('createUpdateIntermediaryPersonModal.intermediary_person_create') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form @submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                <template v-if="availableIntermediaryPersons !== null">
                <div class="row mb-3">
                  <div class="col-12 col-md-6 form-group">
                    <label for="surnameInput" class="form-label">{{ t('createUpdateIntermediaryPersonModal.surname') }}</label>
                    <input v-model="intermediaryPerson.surname" id="surnameInput" class="form-control"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="firstnameInput" class="form-label">{{ t('createUpdateIntermediaryPersonModal.name') }}</label>
                    <input v-model="intermediaryPerson.firstname" id="firstnameInput" class="form-control"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="emailInput" class="form-label">{{ t('createUpdateIntermediaryPersonModal.email') }}</label>
                    <input v-model="intermediaryPerson.email" id="emailInput" class="form-control" type="email" required/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="phoneInput" class="form-label">{{ t('createUpdateIntermediaryPersonModal.phone_number') }}</label>
                    <input v-model="intermediaryPerson.phone" id="phoneInput" class="form-control" type="text" required/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="parentId" class="form-label">{{ t('createUpdateIntermediaryPersonModal.manager') }}</label>
                    <select class="form-select" v-model="intermediaryPerson.parent_id">
                      <option v-for="(availableIntermediaryPerson, index) in availableIntermediaryPersons" :key="index" :value="availableIntermediaryPerson.id">{{ availableIntermediaryPerson.label }}</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="roleInput" class="form-label">{{ t('createUpdateIntermediaryPersonModal.role') }}</label>
                    <select class="form-select" v-model="intermediaryPerson.role" :disabled="!canAssignRole" required>
                      <option v-for="(availableRole, index) in availableRoles" :key="index" :value="availableRole.value" :disabled="isRoleOptionDisabled(availableRole.value)">{{ availableRole.label }}</option>
                    </select>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="referralCodeInput" class="form-label">{{ t('createUpdateIntermediaryPersonModal.referral_code') }}</label>
                    <input v-model="intermediaryPerson.referral_code" id="referralCodeInput" class="form-control" type="text"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="externalIdInput" class="form-label">{{ t('createUpdateIntermediaryPersonModal.external_id') }}</label>
                    <input v-model="intermediaryPerson.external_id" id="externalIdInput" class="form-control" type="text"/>
                  </div>
                </div>

                <div v-if="visibleFields.includes('mentor_firstname') || visibleFields.includes('mentor_surname')" class="row">
                  <div v-if="visibleFields.includes('mentor_firstname')" class="col-12 col-md-6 form-group">
                    <label for="mentorFirstnameInput" class="form-label">{{ t('createUpdateIntermediaryPersonModal.mentor_firstname') }}</label>
                    <input v-model="intermediaryPerson.mentor_firstname" id="mentorFirstnameInput" class="form-control" type="text"/>
                  </div>
                  <div v-if="visibleFields.includes('mentor_surname')" class="col-12 col-md-6 form-group">
                    <label for="mentorSurnameInput" class="form-label">{{ t('createUpdateIntermediaryPersonModal.mentor_surname') }}</label>
                    <input v-model="intermediaryPerson.mentor_surname" id="mentorSurnameInput" class="form-control" type="text"/>
                  </div>
                </div>

                <div v-if="visibleFields.includes('registration_number')" class="row">
                  <div class="col-12 form-group">
                    <label for="registrationNumberInput" class="form-label">{{ t('createUpdateIntermediaryPersonModal.registration_number') }}</label>
                    <input v-model="intermediaryPerson.registration_number" id="registrationNumberInput" class="form-control" type="text"/>
                  </div>
                </div>

                <template v-if="!intermediaryPerson?.id">
                  <div class="row mb-3">
                    <div class="col-12 form-group">
                      <label for="passwordInput" class="form-label">{{ t('createUpdateIntermediaryPersonModal.password') }}</label>
                      <input v-model="intermediaryPerson.password" id="passwordInput" class="form-control" type="password" :required="!intermediaryPerson.id"/>
                    </div>
                  </div>
                  <div class="row mb-3">
                    <div class="col-12 form-group">
                      <label for="passwordConfirmationInput" class="form-label">{{ t('createUpdateIntermediaryPersonModal.confirm_password') }}</label>
                      <input v-model="intermediaryPerson.password_conf" id="passwordConfirmationInput" class="form-control" type="password" :required="!intermediaryPerson.id"/>
                      <span v-if="passwordMismatch" class="text-danger">{{ t('createUpdateIntermediaryPersonModal.passwords_not_match') }}</span>
                    </div>
                  </div>
                </template>
                </template>
              </div>

              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('createUpdateIntermediaryPersonModal.cancel') }}</button>
                <button class="btn btn-danger btn-md" @click.prevent="deleteIntermediaryPerson">{{ t('createUpdateIntermediaryPersonModal.delete') }}</button>
                <button class="btn btn-primary btn-md" type="submit">{{ t('createUpdateIntermediaryPersonModal.save') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

